import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams, type Location } from 'react-router-dom'

import { useClock, useConnectors, useTasks } from '../lib/data'
import { takeSharedText } from '../lib/shared-audio'
import { DayPrototype } from '../routes/DayPrototype'
import { RecordLecture } from '../routes/RecordLecture'
import { Recording } from '../routes/Recording'
import { CaptureSheet } from './capture/CaptureSheet'
import { RecordOverlay } from './capture/RecordOverlay'
import { EMPTY_DRAFT, hasContent, withText, withType, type CaptureDraft } from './lib/captureDraft'
import { DesktopOnly } from './screens/DesktopOnly'
// Reading back on the phone (JRN-1c). Their own import lines, this file's convention.
import { MobileJournalArchive } from './screens/MobileJournalArchive'
import { MobileJournalDay } from './screens/MobileJournalDay'
import { MobileCalendarDay } from './screens/MobileCalendarDay'
import { MobileChat } from './screens/MobileChat'
import { MobileCourse } from './screens/MobileCourse'
import { MobileNotificationsSettings } from './screens/NotificationsSettings'
import { MobileRecordings } from './screens/MobileRecordings'
import { MobileSchool } from './screens/MobileSchool'
import { MobileTasks } from './screens/MobileTasks'
import { MobileDay } from './screens/MobileDay'
import { CaptureProvider } from './shell/CaptureContext'
import { tabKey, TABS, type TabKey } from './shell/nav'
import { useKeyboardOpen } from './shell/keyboard'
import { NavStage } from './shell/NavStage'
import { shellSizeVars } from './shell/sizes'
import { emitTabReselect } from './shell/reselect'
import { TabBar } from './shell/TabBar'
import type { MenuItem } from './shell/tabGesture'

// ============================================================================
// THE MOBILE COMPANION — docs/design/handoff-mobile/README.md.
//
// "It is not a reflow of the desktop screens. It is a deliberately smaller
// product with one job: capture."
//
// WHAT IS HERE: Day (V10 boards 01/02 — it replaced Today 1a/1b in Phase 2), the capture sheet (1c), the recording surface
// (1d), School (1e), one course (1f), Tasks (1g), Chat (1h). Plus `/record`,
// which was already mobile-first and is where the Android share sheet drops a
// lecture recording.
//
// WHAT IS NOT, AND WHY IT IS A SCREEN RATHER THAN A REDIRECT: the routes the
// README lists as deliberately absent land on DesktopOnly, which names the route
// and says where it lives. A URL that silently bounced to Today would be
// indistinguishable from a broken link.
//
// `/calendar` CAME OFF THAT LIST (DAY-1). The phone reads ONE DAY as a list; the
// week grid, which is what needed a pointer and a week's width, is still the
// desktop's and DesktopOnly still says so. See screens/MobileCalendarDay.tsx.
//
// THE ONE EXCEPTION, and it is an exception about a fact rather than a taste:
// `/settings/notifications`. A Web Push subscription belongs to the browser it
// was created in, so the desktop cannot turn notifications on for this phone —
// only this phone can. That route renders the SAME settings component the desktop
// route renders, inside this shell's chrome. Everything else under `/settings`
// still lands on DesktopOnly. See screens/NotificationsSettings.tsx.
//
// SCREENS 1i–1k (home-screen widget, notification shade, lock-screen quick
// journal) ARE NOT BUILT AND CANNOT BE. All three are Android OS surfaces: a
// widget is an app-widget provider, the shade needs notification channels, and
// the lock screen needs a keyguard view. A PWA has none of those, and faking any
// of them inside the app would be drawing a picture of a thing rather than
// building it. They wait for the native app (Phase 9). The lock screen's privacy
// invariant — WRITE-ONLY, never rendering entry text, task titles beyond the
// class name, chat content, or notification bodies from the journal domain —
// belongs to whoever builds it, and is repeated in the port report so it is not
// discovered fresh a year from now.
//
// ── V10 SHELL (owner, 2026-10-18; docs/design/handoff-mobile-v10/) ──────────
// The shell around the screens is the V10 redesign's: its own scoped tokens
// (`[data-shell='mobile']` in index.css), tab bar B with five tabs — Day ·
// School · Chat · Reminders · Notes — and + on the right (shell/TabBar.tsx),
// and a nav layer that fades between tabs and pushes details in from the right
// (shell/NavStage.tsx). The SCREENS behind the tabs keep their current look
// until their own phases; only the chrome around them changed.
// ============================================================================

export function MobileApp() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const barRef = useRef<HTMLDivElement>(null)

  // ── The two capture surfaces, mounted once, above the routes ──────────────
  const [sheetOpen, setSheetOpen] = useState(false)
  /** 'hold' — recording while a finger is down (the composer's circle); 'tap' — started from the + menu, stopped on screen. */
  const [recording, setRecording] = useState<false | 'hold' | 'tap'>(false)
  /** It DRAFTS: closing the sheet keeps what was typed (README §1c). */
  const [draft, setDraft] = useState<CaptureDraft>(EMPTY_DRAFT)

  const surfaces = useRef({
    openSheet: () => setSheetOpen(true),
    startRecording: () => {
      setSheetOpen(false)
      setRecording('hold')
    },
    // The recording surface stops itself on release; this only takes the
    // surface down. `RecordOverlay` owns the upload and closes on its own when
    // the memo is safely queued.
    stopRecording: () => setRecording(false),
    openReminder: (text: string) => {
      setDraft((d) => {
        const next = hasContent(d) ? `${d.text.replace(/\s+$/, '')}\n${text}` : text
        return withType({ ...withText(d, next), typeOverride: null, touched: false }, null, 'reminder')
      })
      setSheetOpen(true)
    },
  }).current

  /**
   * THE + MENU'S FOUR ITEMS (board 03), wired to what exists today. The boards' own targets —
   * quick add (10), the note editor (14), a new chat thread (18) — arrive in their phases.
   *   · Voice memo → the recording surface, recording from the moment of release; it stays up
   *     until Stop, because no finger is holding it.
   *   · Reminder   → the capture sheet with its type set to Reminder — the same override the
   *     sheet's own Reminder chip sets, so its When presets are showing.
   *   · Note       → the capture sheet as it always opens: detection decides, nothing preset.
   *   · Ask        → Chat.
   */
  const pick = useCallback(
    (item: MenuItem) => {
      if (item === 'voice') {
        setSheetOpen(false)
        setRecording('tap')
        return
      }
      if (item === 'ask') {
        navigate('/chat')
        return
      }
      setDraft((d) => {
        const untouched = { ...d, typeOverride: null, touched: false }
        return item === 'reminder' ? withType(untouched, null, 'reminder') : untouched
      })
      setSheetOpen(true)
    },
    [navigate],
  )

  // The soft keyboard is up: the bar steps down out of its way (shell/keyboard.ts).
  const keyboardOpen = useKeyboardOpen()

  const active: TabKey = tabKey(pathname)
  const select = useCallback(
    (tab: TabKey) => {
      // Re-tapping the tab you are on scrolls it to the top; it does not navigate or replay.
      if (tab === active) {
        emitTabReselect(tab)
        return
      }
      navigate(TABS.find((t) => t.key === tab)!.to)
    },
    [active, navigate],
  )

  useShareTargetDraft(setDraft, surfaces.openSheet)

  const { data: clock } = useClock()
  const { data: openTasks } = useTasks({ done: false })
  const { data: connectors } = useConnectors()

  // The two attention dots, and both are real states rather than decoration.
  const tasksNeedAttention = Boolean(
    clock && openTasks?.some((t) => t.due !== null && t.due < clock.date),
  )
  const schoolNeedsAttention = Boolean(
    connectors?.some((c) => c.status === 'error' && c.outage !== null),
  )

  // A failed read is not an alarm: when either read throws, its dot stays off
  // rather than lighting up over an absence.

  const renderRoutes = (location: Location) => (
    <Routes location={location}>
      {/* DAY (V10 boards 01/02) — the app's first screen (owner, 2026-10-18). */}
      <Route path="/" element={<MobileDay />} />
      <Route path="/today" element={<MobileDay />} />
      {/* THE DAY PAGE, PROTOTYPE — the phone keeps it while the owner redesigns mobile; the
          desktop's `/day` is the designed page now (routes/Day.tsx). It belongs to the Day
          tab (see `tabKey` in shell/nav.ts), and is reached from the Day mail sheet's "All mail" link,
          never a tab of its own. */}
      <Route path="/day" element={<DayPrototype />} />

      <Route path="/school" element={<MobileSchool />} />
      {/* BEFORE the :courseCode route on purpose. React Router ranks a static segment above
          a dynamic one, so this would win either way — but the ordering is the thing a
          reader checks first when a new /school/<word> route stops working. */}
      <Route path="/school/recordings" element={<MobileRecordings />} />
      <Route path="/school/:courseCode" element={<MobileCourse />} />
      {/* Study mode is desktop-only and the README says so outright. */}
      <Route path="/school/:courseCode/lecture/:n" element={<DesktopOnly />} />

      <Route path="/tasks" element={<MobileTasks />} />

      <Route path="/chat" element={<MobileChat />} />
      <Route path="/chat/:threadId" element={<MobileChat />} />

      {/* ALREADY MOBILE-FIRST, and the landing spot for an audio share.
          They keep working here: this is the phone they were built for. */}
      <Route path="/record" element={<RecordEntry />} />
      <Route path="/recordings/:id" element={<Recording />} />

      {/* READING BACK IS ON THE PHONE NOW (JRN-1c): a month list of previous days and
          one editable day. Everything else under the journal — the heat map, the tag
          counts, search — is still the desktop's, and DesktopOnly still says so about
          the screens that remain there. */}
      <Route path="/journal" element={<MobileJournalArchive />} />
      <Route path="/journal/:date" element={<MobileJournalDay />} />

      {/* Deliberately absent from mobile — named, not redirected. */}
      <Route path="/learning/*" element={<DesktopOnly />} />
      <Route path="/learning" element={<DesktopOnly />} />
      <Route path="/interests" element={<DesktopOnly />} />
      <Route path="/search" element={<DesktopOnly />} />

      {/* THE DAY, NOT THE WEEK (DAY-1). The week GRID is still the desktop's and
          DesktopOnly still says so; what the phone has is one day read as a list — see
          mobile/screens/MobileCalendarDay.tsx. `?date=` means what it means on the
          desktop, which is why this is the same route and not a new one. */}
      <Route path="/calendar" element={<MobileCalendarDay />} />

      {/* THE ONE SETTINGS SCREEN THE PHONE HAS, and it is FIRST because the catch-all
          below would otherwise swallow it. A Web Push subscription belongs to the browser
          it was made in, so notifications on this phone can only be turned on from this
          phone — see mobile/screens/NotificationsSettings.tsx for the full reasoning. Every
          other settings route still lands on DesktopOnly. */}
      <Route path="/settings/notifications" element={<MobileNotificationsSettings />} />
      <Route path="/settings/*" element={<DesktopOnly />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )

  return (
    <CaptureProvider value={surfaces}>
      <div
        data-shell="mobile"
        data-keyboard={keyboardOpen ? 'true' : undefined}
        className="relative flex h-full min-h-0 flex-col overflow-hidden bg-page"
        style={shellSizeVars()}
      >
        <NavStage
          barRef={barRef}
          bar={(pushed) => (
            <TabBar
              active={active}
              away={pushed ? 'push' : keyboardOpen ? 'keyboard' : null}
              tasksNeedAttention={tasksNeedAttention}
              schoolNeedsAttention={schoolNeedsAttention}
              onSelect={select}
              onPick={pick}
              wrapRef={barRef}
            />
          )}
        >
          {renderRoutes}
        </NavStage>

        <CaptureSheet
          open={sheetOpen}
          draft={draft}
          onDraft={setDraft}
          onClose={() => setSheetOpen(false)}
          onRecord={surfaces.startRecording}
        />

        <RecordOverlay
          open={recording !== false}
          held={recording === 'hold'}
          onClose={surfaces.stopRecording}
        />
      </div>
    </CaptureProvider>
  )
}

/**
 * `/record` ON THE PHONE, WHICH IS NOW TWO SCREENS BEHIND ONE ADDRESS.
 *
 * THE BUG THIS FIXES, in the owner's words (2026-09-15): the "NEU C61 starts in 10 min — tap to
 * record" notification opens `/record?course=NEU%20C61&occurrence=…` (the URL is minted by
 * apps/worker/src/lib/notifications.ts and this repo's web half must not change it), and `/record`
 * is the FILE-UPLOAD screen. He tapped a recording prompt and landed on a file picker, with no
 * route back to anything that records.
 *
 * SO THE ADDRESS DISPATCHES, and the rule is a fact rather than a preference:
 *   · `?shared=` — the share-target service worker staged a file and redirected here
 *     (public/share-target-sw.js writes exactly `1`, `empty` or `failed`). That flow is unchanged:
 *     it renders `RecordLecture` exactly as it did, because a recording is sitting in the Cache
 *     API waiting for this screen to pick it up.
 *   · `?upload=1` — the explicit way back to the picker, which is what the Recordings screen
 *     links to. A deliberate choice, not an inference.
 *   · anything else, including the notification's `?course=` — Recordings.
 *
 * THE DECISION IS LATCHED, and it has to be: `RecordLecture` strips `?shared` out of the URL on
 * mount (it is a one-shot marker), which would otherwise flip this dispatcher to Recordings
 * mid-share and unmount the screen that was holding the file.
 *
 * NOTHING IS LOST WHEN THE MARKER IS MISSING — a share staged by an older service worker carries
 * no marker at all. The queue's driver imports every staged share on every pass regardless of
 * which screen is mounted (lib/data/index.ts → `useUploadQueue`), and Recordings renders that same
 * queue. The recording appears either way.
 *
 * The DESKTOP `/record` is untouched: this dispatcher exists only in the mobile route tree.
 */
function RecordEntry() {
  const [params] = useSearchParams()
  const [staged] = useState(() => params.get('shared') !== null || params.get('upload') !== null)
  return staged ? <RecordLecture /> : <MobileRecordings />
}

/**
 * THE ANDROID SHARE SHEET, arriving with words rather than a file.
 *
 * The service worker parked the shared text in the Cache API and redirected to
 * `/?shared=text` (public/share-target-sw.js). This takes it back out — once —
 * and opens the capture sheet with it already in the field, which is the whole
 * point: a link shared from Chrome should be two taps from being a clip.
 *
 * The marker is removed from the URL immediately, so a reload does not re-open a
 * sheet over a share that has already been consumed.
 */
function useShareTargetDraft(
  setDraft: (draft: CaptureDraft) => void,
  openSheet: () => void,
): void {
  const [params, setParams] = useSearchParams()
  const marker = params.get('shared')

  const consume = useCallback(async () => {
    const text = await takeSharedText()
    if (!text) return
    setDraft(withText(EMPTY_DRAFT, text))
    openSheet()
  }, [openSheet, setDraft])

  useEffect(() => {
    if (marker !== 'text') return
    const merged = new URLSearchParams(params)
    merged.delete('shared')
    setParams(merged, { replace: true })
    void consume()
    // `params` and `setParams` change identity on every navigation; keying this
    // on the marker alone is what makes it run once per share.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker])
}
